import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { computed } from "mobx"
import { observer } from "mobx-react-lite"
import { Button, Form } from "antd"

import { FormContainer } from "@src/app/components/FormContainer"
import { notify } from "@src/app/components/notify"
import { configStore } from "@src/app/configStore"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { CheckOutCode } from "@src/pages/checkoutUpsert/ui/CheckOutCode"
import CheckoutUpsertLeft from "@src/pages/checkoutUpsert/ui/CheckoutUpsertLeft"
import CheckoutUpsertRight from "@src/pages/checkoutUpsert/ui/CheckoutUpsertRight"
import { catchErrors } from "@src/shared/api"
import { useCatchFormErrors } from "@src/shared/form/base_error"
import css from "./index.module.css"
import { CheckoutEntity } from "./store"

export const CheckoutUpsertPage = observer(() => {
  const [form] = Form.useForm()

  const { id } = useParams()

  const navigate = useNavigate()

  const {
    config: { payment_page_url },
  } = configStore

  const [checkoutEntity] = useState(() => new CheckoutEntity())

  const [disabledSubmit, setDisabledSubmit] = useState(
    checkoutEntity.isReadonly,
  )

  const [title, isLoadingForm, isDisabled] = computed(() => {
    const isLoading = [
      checkoutEntity.projects.isLoading,
      checkoutEntity.isLoading,
      configStore.isLoading,
    ].some(Boolean)

    return [
      checkoutEntity.isNew ? "Create a checkout" : "Edit checkout",
      isLoading,
      isLoading || checkoutEntity.isReadonly,
    ] as [string, boolean, boolean]
  }).get()

  const { baseError, submit } = useCatchFormErrors(form, async () => {
    return checkoutEntity
      .update(form.getFieldsValue())
      .then(() => {
        navigate(paths.checkouts)

        notify.success(
          checkoutEntity.isNew ? "Checkout created" : "Checkout updated",
        )
      })
      .catch(catchErrors)
  })

  useEffect(() => {
    const isEdit = id && id !== "new"

    checkoutEntity.setIsReadonly(Boolean(isEdit))

    if (isEdit) {
      checkoutEntity.fetch(id).then(() => {
        form.setFieldsValue(checkoutEntity.toForm)
      })
    } else {
      form.setFieldsValue({ pricing_type: "fixed_price" })

      setDisabledSubmit(true)
    }
  }, [id])

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)

    const { amount, description, name, pricing_type, project_id, symbol } =
      form.getFieldsValue()

    const isValid = () => {
      if (!checkoutEntity.isNew) return true

      if (pricing_type === "fixed_price") {
        return Boolean(amount && symbol && name && description && project_id)
      } else {
        return Boolean(name && description && project_id)
      }
    }

    setDisabledSubmit(hasErrors || !isValid())
  }

  const headerButton = computed<JSX.Element>(() => {
    if (checkoutEntity.isNew) {
      return (
        <Button
          disabled={disabledSubmit}
          id="GENERATED_ID_CHECKOUTUPSERT_BUTTON_CONFIRM_1"
          loading={checkoutEntity.isLoading}
          onClick={() => submit(form.getFieldsValue())}
          type="primary"
        >
          Confirm
        </Button>
      )
    }

    return (
      <Button
        disabled={disabledSubmit}
        ghost={checkoutEntity.isReadonly}
        id="GENERATED_ID_CHECKOUTUPSERT_BUTTON_VOID_1"
        loading={checkoutEntity.isLoading}
        onClick={() =>
          checkoutEntity.isReadonly
            ? checkoutEntity.setIsReadonly(false)
            : submit(form.getFieldsValue())
        }
        type="primary"
      >
        {checkoutEntity.isReadonly ? "Edit" : "Save"}
      </Button>
    )
  }).get()

  return (
    <DefaultPageLayout
      button={headerButton}
      title={title}
      url={paths.checkouts}
    >
      <>
        <FormContainer
          baseError={baseError}
          className={css.wrapper}
          disabled={isDisabled}
          form={form}
          name="checkout"
          onFieldsChange={handleFormChange}
          size="large"
        >
          <CheckoutUpsertLeft
            isNew={checkoutEntity.isNew}
            projects={checkoutEntity.projects}
          />

          <CheckoutUpsertRight
            disabled={isDisabled}
            image={checkoutEntity.image}
            isNew={checkoutEntity.isNew}
          />
        </FormContainer>

        {checkoutEntity.isReadonly && (
          <CheckOutCode
            id={checkoutEntity.id as string}
            isLoading={isLoadingForm}
            paymentPageUrl={payment_page_url as string}
          />
        )}
      </>
    </DefaultPageLayout>
  )
})
